import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { PageHeader } from '@/components/common/PageHeader'
import { lgpdService, MeusDadosResponse } from '@/services/lgpdService'
import { perfilService } from '@/services/perfilService'
import { solicitacoesTitularService } from '@/services/solicitacoesTitularService'
import {
  ConfiguracaoLgpd,
  ESTADO_SOLICITACAO_LABEL,
  MinhaSolicitacao,
  TIPO_SOLICITACAO_LABEL,
} from '@/types/privacidade'
import { useAuth } from '@/hooks/useAuth'
import { tokens } from '@/theme'

/**
 * Meus Dados no padrão do Figma (Onda 8 / A16): cada dado pessoal em uma linha
 * `valor · estado · Editar`, e os consentimentos como lista com estado e data.
 *
 * Só estados reais: "Verificado" no e-mail vem de `perfil.emailVerificado` (Keycloak).
 * Nome e telefone não têm verificação no backend — a linha diz "Sem verificação" em vez
 * de fingir; o telefone vem de `GET /v1/perfil` (mantido pelo próprio usuário). Nome e
 * e-mail são identidade: quem altera é o ADMIN em Usuários e Acessos, então o "Editar"
 * deles explica isso em vez de abrir um formulário que não salva.
 */
export const ROTA_EDITAR_PERFIL = '/usuarios/meu-acesso?aba=perfil'

type EstadoDado = 'verificado' | 'naoVerificado' | 'semVerificacao'

const CHIP_ESTADO: Record<EstadoDado, { label: string; color: 'success' | 'warning' | 'default' }> = {
  verificado: { label: 'Verificado', color: 'success' },
  naoVerificado: { label: 'Não verificado', color: 'warning' },
  semVerificacao: { label: 'Sem verificação', color: 'default' },
}

const LinhaDado: FC<{
  rotulo: string
  valor: string | null | undefined
  estado: EstadoDado
  editar: ReactNode
}> = ({ rotulo, valor, estado, editar }) => {
  const chip = CHIP_ESTADO[estado]
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      data-testid={`dado-${rotulo.toLowerCase().replace(/[^a-z]/g, '')}`}
      sx={{ py: 1.25 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
          {rotulo}
        </Typography>
        <Typography noWrap>{valor || '—'}</Typography>
      </Box>
      <Chip
        size="small"
        label={chip.label}
        color={chip.color}
        variant={estado === 'semVerificacao' ? 'outlined' : 'filled'}
        icon={estado === 'verificado' ? <CheckCircleOutlineIcon /> : undefined}
      />
      <Typography sx={{ color: tokens.textSecondary, display: { xs: 'none', sm: 'block' } }}>·</Typography>
      {editar}
    </Stack>
  )
}

const MeusDadosPage: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [dados, setDados] = useState<MeusDadosResponse | null>(null)
  const [telefone, setTelefone] = useState<string | null | undefined>(undefined)
  const [versaoAtual, setVersaoAtual] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [confirmAnonOpen, setConfirmAnonOpen] = useState(false)
  const [confirmTexto, setConfirmTexto] = useState('')
  const [anonimizando, setAnonimizando] = useState(false)
  /**
   * AN-12 §4.3: com `LGPD_ANONIMIZACAO_IMEDIATA=false` o servidor não anonimiza na hora —
   * registra a solicitação (202 `{ status: "solicitada", protocolo }`). Hoje a flag nasce `true`
   * (ONDA5, decisão 1) e o comportamento é o de sempre (200 `anonimizado`, desconecta).
   */
  const [protocoloAnonimizacao, setProtocoloAnonimizacao] = useState<string | null>(null)
  /** `null` = indisponível (backend sem o endpoint ainda): o bloco simplesmente não aparece. */
  const [minhasSolicitacoes, setMinhasSolicitacoes] = useState<MinhaSolicitacao[] | null>(null)
  /** `GET /v1/lgpd/configuracao`; `null` = indisponível (sem prazo e texto atual). */
  const [config, setConfig] = useState<ConfiguracaoLgpd | null>(null)
  const [cancelando, setCancelando] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setLoading(true)
    setErro(null)
    try {
      const [d, perfil, versao, solicitacoes, configuracao] = await Promise.all([
        lgpdService.meusDados(),
        // Telefone e versão vigente são complementares: falha neles não esconde os direitos do titular.
        perfilService.obter().catch(() => null),
        lgpdService.versao().catch(() => null),
        solicitacoesTitularService.minhas().catch(() => null),
        solicitacoesTitularService.configuracao().catch(() => null),
      ])
      setDados(d)
      setMinhasSolicitacoes(solicitacoes)
      setConfig(configuracao)
      setTelefone(perfil ? (perfil.telefone ?? null) : undefined)
      setVersaoAtual(versao)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar seus dados')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    carregar()
  }, [carregar])

  const exportar = async () => {
    try {
      const blob = await lgpdService.exportarBlob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `meus-dados-originei-${dayjs().format('YYYY-MM-DD')}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao exportar', { variant: 'error' })
    }
  }

  const confirmarAnonimizacao = async () => {
    if (confirmTexto.trim().toUpperCase() !== 'ANONIMIZAR') {
      enqueueSnackbar('Digite ANONIMIZAR para confirmar', { variant: 'warning' })
      return
    }
    setAnonimizando(true)
    try {
      const { status, protocolo, mensagem } = await lgpdService.anonimizar()
      if (status === 'solicitada') {
        // Virou solicitação: nada foi anonimizado ainda e a sessão continua.
        setProtocoloAnonimizacao(protocolo)
        setConfirmAnonOpen(false)
        setConfirmTexto('')
        enqueueSnackbar(mensagem || `Solicitação registrada — protocolo ${protocolo ?? ''}`, { variant: 'success' })
        solicitacoesTitularService
          .minhas()
          .then(setMinhasSolicitacoes)
          .catch(() => undefined)
        return
      }
      enqueueSnackbar(
        'Sua conta foi anonimizada. Você será desconectado.',
        { variant: 'success', autoHideDuration: 5000 }
      )
      setTimeout(() => logout(), 2500)
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : 'Falha ao anonimizar', { variant: 'error' })
    } finally {
      setAnonimizando(false)
    }
  }

  const cancelarSolicitacao = async (protocolo: string) => {
    setCancelando(protocolo)
    try {
      await solicitacoesTitularService.cancelarMinha(protocolo)
      enqueueSnackbar(`Solicitação ${protocolo} cancelada`, { variant: 'success' })
      setMinhasSolicitacoes(await solicitacoesTitularService.minhas().catch(() => minhasSolicitacoes))
    } catch (e) {
      const codigo = (e as { data?: { errorCode?: string } })?.data?.errorCode
      enqueueSnackbar(
        codigo === 'SOLICITACAO_NAO_CANCELAVEL'
          ? 'Esta solicitação já não pode ser cancelada.'
          : e instanceof Error
            ? e.message
            : ((e as { message?: string })?.message ?? 'Falha ao cancelar'),
        { variant: 'error' }
      )
    } finally {
      setCancelando(null)
    }
  }

  // Anonimização via solicitação (flag desligada): o texto do aviso muda — nada acontece na hora.
  const viaSolicitacao = config?.anonimizacaoImediata === false

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (erro || !dados) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{erro ?? 'Dados não encontrados'}</Alert>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Meus Dados"
        subtitle="Direitos do titular — Art. 18 da LGPD"
        action={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={carregar}>
              Atualizar
            </Button>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportar}>
              Exportar JSON
            </Button>
          </Stack>
        }
      />

      <Stack spacing={3}>
        <Card>
          <CardHeader
            title="Seus dados pessoais"
            subheader="Art. 18, II — confirmação da existência de tratamento"
            action={
              <Chip
                label={dados.perfil.ativo ? 'Conta ativa' : 'Conta inativa'}
                size="small"
                color={dados.perfil.ativo ? 'success' : 'default'}
                sx={{ mt: 1, mr: 1 }}
              />
            }
          />
          <CardContent sx={{ pt: 0 }}>
            <Stack divider={<Divider />} data-testid="dados-pessoais">
              <LinhaDado
                rotulo="Nome"
                valor={dados.perfil.nome}
                estado="semVerificacao"
                editar={
                  <Tooltip title="Nome e e-mail são identidade: quem altera é o administrador, em Usuários e Acessos.">
                    <span>
                      <Button size="small" disabled>
                        Editar
                      </Button>
                    </span>
                  </Tooltip>
                }
              />
              <LinhaDado
                rotulo="E-mail"
                valor={dados.perfil.email}
                estado={dados.perfil.emailVerificado ? 'verificado' : 'naoVerificado'}
                editar={
                  <Tooltip title="Nome e e-mail são identidade: quem altera é o administrador, em Usuários e Acessos.">
                    <span>
                      <Button size="small" disabled>
                        Editar
                      </Button>
                    </span>
                  </Tooltip>
                }
              />
              <LinhaDado
                rotulo="Telefone"
                valor={telefone === undefined ? 'Indisponível no momento' : telefone}
                estado="semVerificacao"
                editar={
                  <Button size="small" onClick={() => navigate(ROTA_EDITAR_PERFIL)} aria-label="Editar telefone">
                    Editar
                  </Button>
                }
              />
            </Stack>
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">Identificador interno</Typography>
                <Typography sx={{ fontFamily: 'monospace' }}>{dados.usuarioId}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Atributos adicionais</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, fontFamily: 'monospace', fontSize: 12, mt: 0.5 }}>
                  {Object.keys(dados.atributos ?? {}).length === 0
                    ? '—'
                    : JSON.stringify(dados.atributos, null, 2)}
                </Paper>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title="Consentimentos"
            subheader="Art. 7º — base legal do tratamento"
          />
          <CardContent sx={{ pt: 0 }}>
            <Stack divider={<Divider />} data-testid="consentimentos">
              {(() => {
                const c = dados.consentimento
                const desatualizado = c.aceito && !!versaoAtual && !!c.versao && c.versao !== versaoAtual
                const chip = !c.aceito
                  ? { label: 'Pendente', color: 'warning' as const }
                  : desatualizado
                    ? { label: `Nova versão ${versaoAtual}`, color: 'warning' as const }
                    : { label: 'Aceito', color: 'success' as const }
                return (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    sx={{ py: 1.25 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={600}>Termos de uso e Política de privacidade</Typography>
                      <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                        {c.aceito
                          ? `Versão ${c.versao ?? '—'} · aceita em ${c.aceitoEm ? dayjs(c.aceitoEm).format('DD/MM/YYYY HH:mm') : '—'}${c.ip ? ` · IP ${c.ip}` : ''}`
                          : 'Você ainda não registrou o aceite.'}
                      </Typography>
                    </Box>
                    <Chip size="small" label={chip.label} color={chip.color} data-testid="consentimento-estado" />
                    <Typography sx={{ color: tokens.textSecondary, display: { xs: 'none', sm: 'block' } }}>·</Typography>
                    <Button size="small" onClick={() => navigate('/termos')}>
                      Ler termos
                    </Button>
                  </Stack>
                )
              })()}
            </Stack>
            <Typography variant="caption" sx={{ color: tokens.textSecondary, display: 'block', mt: 1.5 }}>
              O aceite é pedido no acesso ao portal sempre que uma nova versão entra em vigor.
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
          <CardHeader
            title="Eliminar minha conta"
            subheader="Art. 18, VI — anonimização ou eliminação"
            sx={{ '& .MuiCardHeader-title': { color: 'error.main' } }}
          />
          <CardContent>
            {viaSolicitacao ? (
              <Alert severity="info" sx={{ mb: 2 }} data-testid="aviso-anonimizacao-solicitacao">
                Seu pedido será registrado como solicitação e analisado pelo encarregado (DPO). Quando for
                executado, seu nome e e-mail serão substituídos por valores anonimizados e seu acesso será
                desabilitado. <strong>Depois de executado, não pode ser desfeito.</strong>
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Esta ação substituirá seu nome e e-mail por valores anonimizados, desabilitará seu acesso
                e desconectará você do sistema. <strong>Não pode ser desfeita.</strong> Dados das operações
                em que você atuou (oportunidades, reuniões) são preservados em forma de identificadores
                opacos para cumprir obrigações legais e regulatórias (Art. 16, I).
              </Alert>
            )}
            {config?.prazoRespostaDias != null && (
              <Typography variant="body2" sx={{ mb: 2, color: tokens.textSecondary }} data-testid="prazo-resposta">
                Prazo de resposta às solicitações do titular: {config.prazoRespostaDias} dias.
              </Typography>
            )}
            {protocoloAnonimizacao && (
              <Alert severity="success" sx={{ mb: 2 }} data-testid="protocolo-anonimizacao">
                Sua solicitação de anonimização foi registrada com o protocolo{' '}
                <strong>{protocoloAnonimizacao}</strong>. O encarregado (DPO) vai analisá-la; sua conta continua
                ativa até a execução. Guarde o protocolo para acompanhar.
              </Alert>
            )}
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={() => setConfirmAnonOpen(true)}
              disabled={!!protocoloAnonimizacao}
            >
              Solicitar anonimização da minha conta
            </Button>
          </CardContent>
        </Card>

        {minhasSolicitacoes && minhasSolicitacoes.length > 0 && (
          <Card>
            <CardHeader title="Minhas solicitações" subheader="Pedidos registrados como titular (Art. 18)" />
            <CardContent sx={{ pt: 0 }}>
              <Stack divider={<Divider />} data-testid="minhas-solicitacoes">
                {minhasSolicitacoes.map((sol) => (
                  <Stack
                    key={sol.protocolo}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    sx={{ py: 1.25 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                        {sol.protocolo}
                      </Typography>
                      <Typography variant="body2" sx={{ color: tokens.textSecondary }}>
                        {TIPO_SOLICITACAO_LABEL[sol.tipo] ?? sol.tipo} · recebida em{' '}
                        {dayjs(sol.recebidaEm).format('DD/MM/YYYY')}
                        {sol.prazoRespostaEm
                          ? ` · resposta prevista até ${dayjs(sol.prazoRespostaEm).format('DD/MM/YYYY')}`
                          : ''}
                      </Typography>
                    </Box>
                    <Chip size="small" label={ESTADO_SOLICITACAO_LABEL[sol.estado] ?? sol.estado} />
                    {sol.cancelavel && (
                      <Button
                        size="small"
                        disabled={cancelando === sol.protocolo}
                        onClick={() => cancelarSolicitacao(sol.protocolo)}
                        aria-label={`Cancelar ${sol.protocolo}`}
                      >
                        Cancelar
                      </Button>
                    )}
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
          Documento gerado em {dayjs(dados.geradoEm).format('DD/MM/YYYY HH:mm:ss')}
        </Typography>
      </Stack>

      <Dialog open={confirmAnonOpen} onClose={() => setConfirmAnonOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0, fontWeight: 700, color: 'error.main' }}>
          Confirmar anonimização
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {viaSolicitacao
              ? 'Sua solicitação será registrada e, quando executada, não poderá ser desfeita.'
              : 'Esta operação não pode ser desfeita. Sua conta será permanentemente desativada.'}
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Para confirmar, digite <strong>ANONIMIZAR</strong> abaixo:
          </Typography>
          <TextField
            fullWidth
            value={confirmTexto}
            onChange={(e) => setConfirmTexto(e.target.value)}
            placeholder="ANONIMIZAR"
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmAnonOpen(false)} disabled={anonimizando}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmarAnonimizacao}
            disabled={anonimizando || confirmTexto.trim().toUpperCase() !== 'ANONIMIZAR'}
          >
            {anonimizando ? 'Processando…' : 'Confirmar anonimização'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MeusDadosPage
